import {
  workspaceNameSchema,
  workspaceTimezoneSchema,
} from "@postdaily/api/schemas/workspace";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@postdaily/ui/components/alert-dialog";
import { Button } from "@postdaily/ui/components/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@postdaily/ui/components/card";
import {
  Field,
  FieldDescription,
  FieldError,
  FieldGroup,
  FieldLabel,
} from "@postdaily/ui/components/field";
import { Input } from "@postdaily/ui/components/input";
import { Label } from "@postdaily/ui/components/label";
import { Skeleton } from "@postdaily/ui/components/skeleton";
import { Spinner } from "@postdaily/ui/components/spinner";
import { Switch } from "@postdaily/ui/components/switch";
import { useForm } from "@tanstack/react-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  createFileRoute,
  Outlet,
  useNavigate,
  useRouterState,
} from "@tanstack/react-router";
import { Save, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";

import { PageHeader } from "@/components/page-header";
import { resolveDefaultTimezone } from "@/components/scheduling/schedule-time";
import { TimezonePicker } from "@/components/timezone-picker";
import { useCurrentWorkspace } from "@/hooks/use-current-workspace";
import { useTRPC } from "@/utils/trpc";

export const Route = createFileRoute("/(app)/settings")({
  component: RouteComponent,
});

// Settings navigation lives in the sidebar ("Account" and "Workspace
// settings" groups in app-sidebar.tsx) — this layout intentionally has no tab
// bar of its own. Child routes render their own page headers.
function RouteComponent() {
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });

  if (pathname !== "/settings") {
    return <Outlet />;
  }

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        size="lg"
        title="General"
        description="Scheduling defaults and general details for this workspace."
      />
      <WorkspaceSettings />
      <LinkTrackingCard />
      <DeleteWorkspaceCard />
    </div>
  );
}

const workspaceFormSchema = z.object({
  defaultTimezone: workspaceTimezoneSchema,
  name: workspaceNameSchema,
});

function WorkspaceSettings() {
  const { currentWorkspace } = useCurrentWorkspace();

  if (!currentWorkspace) {
    return <Skeleton className="h-72 w-full" />;
  }

  return <WorkspaceSettingsForm key={currentWorkspace.id} />;
}

function WorkspaceSettingsForm() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { currentWorkspace, refresh } = useCurrentWorkspace();
  const updateWorkspace = useMutation(
    trpc.workspaces.update.mutationOptions({
      onSuccess: async () => {
        toast.success("Workspace updated");
        refresh();
        await queryClient.invalidateQueries(trpc.workspaces.list.queryFilter());
      },
      onError: (error) => {
        toast.error(error.message);
      },
    }),
  );
  const form = useForm({
    defaultValues: {
      // Pre-fill the user's local timezone when the workspace is still on the
      // unconfigured "UTC" default, so saving persists a sensible value.
      defaultTimezone: resolveDefaultTimezone(
        currentWorkspace?.defaultTimezone,
      ),
      name: currentWorkspace?.name ?? "",
    },
    onSubmit: async ({ value }) => {
      if (!currentWorkspace) {
        return;
      }

      updateWorkspace.mutate({
        defaultTimezone: value.defaultTimezone,
        name: value.name,
        workspaceId: currentWorkspace.id,
      });
    },
    validators: {
      onSubmit: workspaceFormSchema,
    },
  });

  if (!currentWorkspace) {
    return <Skeleton className="h-72 w-full" />;
  }

  const canEdit =
    currentWorkspace.role === "owner" || currentWorkspace.role === "admin";

  return (
    <Card>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          event.stopPropagation();
          void form.handleSubmit();
        }}
      >
        <CardHeader className="border-b">
          <CardTitle>General</CardTitle>
          <CardDescription>
            These settings apply to scheduling and publishing defaults for this
            workspace.
          </CardDescription>
        </CardHeader>
        <CardContent className="py-6">
          <FieldGroup>
            <form.Field name="name">
              {(field) => {
                const isInvalid =
                  field.state.meta.isTouched && !field.state.meta.isValid;

                return (
                  <Field data-invalid={isInvalid} data-disabled={!canEdit}>
                    <FieldLabel htmlFor={field.name}>Workspace name</FieldLabel>
                    <Input
                      id={field.name}
                      name={field.name}
                      aria-invalid={isInvalid}
                      disabled={!canEdit}
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={(event) =>
                        field.handleChange(event.target.value)
                      }
                    />
                    {isInvalid ? (
                      <FieldError errors={field.state.meta.errors} />
                    ) : null}
                  </Field>
                );
              }}
            </form.Field>
            <form.Field name="defaultTimezone">
              {(field) => {
                const isInvalid =
                  field.state.meta.isTouched && !field.state.meta.isValid;

                return (
                  <Field data-invalid={isInvalid} data-disabled={!canEdit}>
                    <FieldLabel htmlFor={field.name}>
                      Default timezone
                    </FieldLabel>
                    <TimezonePicker
                      id={field.name}
                      aria-invalid={isInvalid}
                      disabled={!canEdit}
                      value={field.state.value}
                      onChange={(timezone) => {
                        field.handleChange(timezone);
                        field.handleBlur();
                      }}
                    />
                    <FieldDescription>
                      Used as the default for scheduling and publishing in this
                      workspace.
                    </FieldDescription>
                    {isInvalid ? (
                      <FieldError errors={field.state.meta.errors} />
                    ) : null}
                  </Field>
                );
              }}
            </form.Field>
          </FieldGroup>
        </CardContent>
        <CardFooter>
          {canEdit ? (
            <form.Subscribe
              selector={(state) => ({
                canSubmit: state.canSubmit,
                isSubmitting: state.isSubmitting,
              })}
            >
              {({ canSubmit, isSubmitting }) => (
                <Button
                  type="submit"
                  disabled={
                    !canSubmit || isSubmitting || updateWorkspace.isPending
                  }
                >
                  <Save data-icon="inline-start" />
                  {updateWorkspace.isPending ? "Saving..." : "Save changes"}
                </Button>
              )}
            </form.Subscribe>
          ) : (
            <p className="text-muted-foreground text-sm">
              Your role can view workspace settings but cannot edit them.
            </p>
          )}
        </CardFooter>
      </form>
    </Card>
  );
}

function DeleteWorkspaceCard() {
  const trpc = useTRPC();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentWorkspace, refresh, setCurrentWorkspaceId, workspaces } =
    useCurrentWorkspace();
  const [confirmName, setConfirmName] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);

  const deleteWorkspace = useMutation(
    trpc.workspaces.delete.mutationOptions({
      onError: (error) => {
        toast.error(error.message);
      },
      onSuccess: async () => {
        toast.success("Workspace deleted");

        const nextWorkspace = workspaces.find(
          (workspace) => workspace.id !== currentWorkspace?.id,
        );

        if (nextWorkspace) {
          setCurrentWorkspaceId(nextWorkspace.id);
        }

        await queryClient.invalidateQueries();
        // With no workspaces left, refresh() re-runs the provider's
        // ensure-default bootstrap so the app lands in a fresh workspace.
        refresh();
        await navigate({ to: "/dashboard" });
      },
    }),
  );

  if (!currentWorkspace || currentWorkspace.role !== "owner") {
    return null;
  }

  const canDelete =
    confirmName.trim().toLowerCase() === currentWorkspace.name.toLowerCase();

  return (
    <>
      <Card className="border-destructive/40">
        <CardHeader>
          <CardTitle className="text-destructive">Delete workspace</CardTitle>
          <CardDescription>
            Permanently deletes {currentWorkspace.name} with all of its posts,
            channels, media, and members. Scheduled posts stop publishing
            immediately. This cannot be undone.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex max-w-sm flex-col gap-2">
            <div className="flex flex-col gap-1">
              <Label htmlFor="confirm-workspace-name">
                Type the workspace name to confirm
              </Label>
              <p className="break-all font-mono text-muted-foreground text-sm">
                {currentWorkspace.name}
              </p>
            </div>
            <Input
              id="confirm-workspace-name"
              value={confirmName}
              onChange={(event) => setConfirmName(event.currentTarget.value)}
              placeholder={currentWorkspace.name}
              autoComplete="off"
            />
          </div>
        </CardContent>
        <CardFooter>
          <Button
            type="button"
            variant="destructive"
            disabled={deleteWorkspace.isPending || !canDelete}
            onClick={() => setConfirmOpen(true)}
          >
            {deleteWorkspace.isPending ? (
              <Spinner data-icon="inline-start" />
            ) : (
              <Trash2 data-icon="inline-start" />
            )}
            Delete workspace
          </Button>
        </CardFooter>
      </Card>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="sm:max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>
              Delete {currentWorkspace.name} permanently?
            </AlertDialogTitle>
            <AlertDialogDescription>
              All posts, connected channels, media, invites, and memberships in
              this workspace will be removed for every member. This cannot be
              undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleteWorkspace.isPending}>
              Keep workspace
            </AlertDialogCancel>
            <AlertDialogAction
              variant="destructive"
              disabled={deleteWorkspace.isPending}
              onClick={() => {
                deleteWorkspace.mutate({ workspaceId: currentWorkspace.id });
                setConfirmOpen(false);
              }}
            >
              {deleteWorkspace.isPending ? (
                <Spinner data-icon="inline-start" />
              ) : null}
              Delete forever
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

/**
 * Campaign tagging for links in published posts.
 *
 * Parameters are applied when a post publishes, not when it is saved: the draft
 * keeps the URL the author typed, and changing these settings still reaches
 * posts that were scheduled before the change.
 */
function LinkTrackingCard() {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const { currentWorkspace } = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id;

  const workspaceQuery = useQuery({
    ...trpc.workspaces.get.queryOptions({ workspaceId: workspaceId ?? "" }),
    enabled: Boolean(workspaceId),
  });

  const [enabled, setEnabled] = useState(false);
  const [source, setSource] = useState("{provider}");
  const [medium, setMedium] = useState("social");
  const [campaign, setCampaign] = useState("");
  const [hydrated, setHydrated] = useState(false);

  const workspace = workspaceQuery.data;

  useEffect(() => {
    if (!workspace || hydrated) {
      return;
    }
    setEnabled(workspace.utmEnabled);
    setSource(workspace.utmSource ?? "");
    setMedium(workspace.utmMedium ?? "");
    setCampaign(workspace.utmCampaign ?? "");
    setHydrated(true);
  }, [workspace, hydrated]);

  const update = useMutation(
    trpc.workspaces.update.mutationOptions({
      onSuccess: async () => {
        toast.success("Link tracking updated");
        await queryClient.invalidateQueries(trpc.workspaces.get.queryFilter());
      },
      onError: (error) => toast.error(error.message),
    }),
  );

  if (!workspaceId || workspaceQuery.isPending) {
    return <Skeleton className="h-72 w-full" />;
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Link tracking</CardTitle>
        <CardDescription>
          Add UTM parameters to every link you publish, so campaign traffic
          shows up in your analytics attributed to the post that earned it.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <FieldGroup>
          <Field orientation="horizontal">
            <Switch
              id="utm-enabled"
              checked={enabled}
              onCheckedChange={(checked) => setEnabled(checked === true)}
            />
            <FieldLabel htmlFor="utm-enabled">
              Tag links in published posts
            </FieldLabel>
          </Field>

          <Field>
            <FieldLabel htmlFor="utm-source">Source</FieldLabel>
            <Input
              id="utm-source"
              value={source}
              disabled={!enabled}
              placeholder="{provider}"
              onChange={(event) => setSource(event.target.value)}
            />
            <FieldDescription>
              <code>{"{provider}"}</code> becomes the network (x, instagram…)
              and <code>{"{handle}"}</code> the channel handle. Leave a field
              blank to skip that parameter.
            </FieldDescription>
          </Field>

          <Field>
            <FieldLabel htmlFor="utm-medium">Medium</FieldLabel>
            <Input
              id="utm-medium"
              value={medium}
              disabled={!enabled}
              placeholder="social"
              onChange={(event) => setMedium(event.target.value)}
            />
          </Field>

          <Field>
            <FieldLabel htmlFor="utm-campaign">Campaign</FieldLabel>
            <Input
              id="utm-campaign"
              value={campaign}
              disabled={!enabled}
              placeholder="Optional — e.g. spring-launch"
              onChange={(event) => setCampaign(event.target.value)}
            />
            <FieldDescription>
              Links that already carry a parameter keep their own value, so a
              tracked link you paste in is never rewritten.
            </FieldDescription>
          </Field>
        </FieldGroup>
      </CardContent>
      <CardFooter>
        <Button
          type="button"
          disabled={update.isPending}
          onClick={() =>
            update.mutate({
              utmCampaign: campaign,
              utmEnabled: enabled,
              utmMedium: medium,
              utmSource: source,
              workspaceId,
            })
          }
        >
          {update.isPending ? <Spinner /> : null}
          Save link tracking
        </Button>
      </CardFooter>
    </Card>
  );
}
